import * as React from 'react';
import * as R from 'ramda';
import ReportRecipientLayout from './ReportRecipientLayout';
import ReportRecipientTable from './ReportRecipientTable';
import LocalDiv from '../localization/LocalDiv';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { css } from '@emotion/core';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import LocalBtn from '../localization/LocalBtn';
import { btnStyle } from '../forms/styles/button';
import { COLORS } from '../globalStyle/colors';
import Spacer from '../layout/Spacer';
import { useAppState } from '../core/redux/hooks';
import SendIcon from '../icons/SendIcon';
import DownloadIcon from '../icons/DownloadIcon';
import { useCurrentUser } from '../auth/hooks';
import { ReportRecipientReport, ReportDeliveryRequest } from './models';
import { useDispatch } from 'react-redux';
import { deliverReportsAction } from './api/deliverReportsAction';
import { useMemo, useState } from 'react';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { useTimedToggle } from '../core/hooks/useTimedToggle';
import { pathCombine } from '../core/api/module';
import { BaseUrl } from '../core/api/endpoints';


const styles = {
  header: css`
    margin: 20px auto 40px;
    font-size: 26px;
    
    ${onMobileDevice} {
      padding-top: 25px;
    }
  `,
  btnRow: {
    root:        [
      FLEX_ROW,
      css`
        justify-content: flex-end;
        margin-top: 12px;
      `,
    ],
    deliverBtn:  btnStyle.transparent(COLORS.BLUE),
    downloadBtn: btnStyle.primary(COLORS.BLUE),
  },
  icon: css`
    padding-top: 10px;
  `,
  linkDisabled:       css`
    cursor: default;
    opacity: .6;

    &:hover { 
      text-decoration: none;
      cursor: default;
    }
  `,
};


type Props = {
  className?: string;
}


/**
 * ReportRecipientDashboard: a component that enables report recipients to handle reports.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientDashboard: React.FC<Props> = props => {
  const dir     = useAppState(state => state.locale.textDirection);

  const currentUser  = useCurrentUser();
  const hasDeliverPermission = useMemo(() => currentUser?.hasReportRecipientDeliverPermission || false, [currentUser]); 
  const recipientId  = useMemo(() => currentUser?.personId ?? -1, [currentUser]);
  const userName  = useMemo(() => currentUser?.userName || '', [currentUser]);

  const [delivered, setDelivered] = useState<number>(0);
  const [downloaded, setDownloaded] = useState<number>(0);

  const [messageVisible, showMessage] = useTimedToggle(false, 5000);

  const dispatch = useDispatch();
  
  const deliverClicked = (report: ReportRecipientReport) => {
    const request = new Array<ReportDeliveryRequest>();
    request.push(
      {
        projectId: report.projectId,
        reportSetId: report.reportSetId,
        participantId: report.participantId,
        archiveDir: report.archiveDir,
        languageId: report.languageId,
        firstName: report.firstName,
        lastName: report.lastName,
        reportId: report.reportId,
      },
    );
    dispatch(deliverReportsAction(request));
    setDownloaded(0);
    setDelivered(1);
    showMessage();
  };

  //TODO - we can implement a redeliverClicked function if needed

  const downloadClicked = () => {
    setDelivered(0);
    setDownloaded(1);
    showMessage();
  };

  const reports = useAppState(state => state.reportRecipientReports || []);

  const selected = useMemo(
    () => reports.filter(r => r.isChecked),
    [reports],
  );

  const downloadAllCount = useMemo(
    () => reports.filter(report => report.fileExists).length,
    [reports],
  );   

  const downloadAllClicked = () => {
    setDelivered(0);
    setDownloaded(downloadAllCount);
    showMessage();
  };

  const downloadSelectedCount = useMemo(
    () => selected.filter(report => report.fileExists).length,
    [selected],
  ); 

  const downloadSelectedClicked = () => {
    setDelivered(0);
    setDownloaded(downloadSelectedCount);
    showMessage();
  };

  const deliverRequest = useMemo(
    () => selected.map(report => ({ 
      projectId: report.projectId,
      reportSetId: report.reportSetId,
      participantId: report.participantId,
      archiveDir: report.archiveDir,
      languageId: report.languageId,
      firstName: report.firstName,
      lastName: report.lastName,
      reportId: report.reportId,
    })),
    [selected],
  );
  
  const deliverSelectedClicked = () => {
    const selectedRequest = deliverRequest;
    dispatch(deliverReportsAction(deliverRequest));
    setDownloaded(0);
    setDelivered(selectedRequest.length);
    showMessage();
  };

  const allSelected = R.all(r => r.isChecked, reports);

  const last = useAppState(state => state.reportRecipientLastOnly);
  const dates = useAppState(state => state.reportRecipientDateRange || 3);
  const projects = useAppState(state => state.reportRecipientSelectedProjects || {});
  const participants = useAppState(state => state.reportRecipientSelectedParticipants || {});

  const downloadURL = useMemo(
    () => {
      if (selected.length === 0) {
        return '#';
      }

      if (allSelected) {
        const projectIds = Object.values(projects || {}).map(a => a.projectId);
        const participantIds = Object.values(participants || {}).map(a => a.participantId);      

        const baseUrl = pathCombine(BaseUrl, '/report/downloadAll');
        const recipient = `/${recipientId}`; 
        const dateRange = `/${dates}`; 
        const projectIdStr = `/${projectIds.length === 0 ? '0' :projectIds.join('|') }`; 
        const participantIdStr = `/${participantIds.length === 0 ? '0' : participantIds.join('|') }`; 
        const lastOnly = `/${last}`; 

        return baseUrl + recipient +  dateRange + projectIdStr + participantIdStr + lastOnly;
      } else {
        const baseUrl = pathCombine(BaseUrl, '/report/downloadSelected');
        const selectedFiles = selected.map(r => r.fileOnly).join('|');

        return baseUrl + '/' + encodeURIComponent(selectedFiles);
      }
    },
    [selected, allSelected, recipientId, last, dates, projects, participants],
  );

  const deliverDisabled = useMemo(
    () => allSelected
      ? reports.length === 0
      : selected.length === 0,
    [allSelected, reports, selected],
  );

  const downloadDisabled = useMemo(
    () => allSelected
    ? reports.filter(r => r.fileExists).length === 0
    : selected.filter(r => r.fileExists).length === 0,
  [allSelected, reports, selected],
  );

  return (
    <ReportRecipientLayout>

      <LocalDiv css={styles.header}>
        <Msg str={Str.ReportPortal} />
      </LocalDiv>

      <ReportRecipientTable
        textDirection={dir}
        recipientId={recipientId}
        userName={userName}
        hasDeliverPermission={hasDeliverPermission}
        deliverClicked={deliverClicked}
        redeliverClicked={deliverClicked}
        downloadClicked={downloadClicked}
        downloadAllClicked={downloadAllClicked}
        delivered={delivered}
        downloaded={downloaded}
        messageVisible={messageVisible}
      />

      <LocalDiv css={styles.btnRow.root}>
        {
          hasDeliverPermission && (
            <LocalBtn 
              str={allSelected ? Str.DeliverAll : Str.DeliverSelected}
              svg={<SendIcon sizePx={14} fill={COLORS.BLUE} />}
              css={styles.btnRow.deliverBtn}
              onClick={deliverSelectedClicked}
              disabled={deliverDisabled}
              />
          )
        }
        <Spacer sizePx={10} />
        <a 
          href={downloadURL}
          css={[FLEX_ROW, styles.btnRow.downloadBtn, downloadDisabled && styles.linkDisabled]}
          onClick={downloadSelectedClicked}
        >
          <DownloadIcon sizePx={14} fill={COLORS.WHITE} css={[FLEX_COLUMN, styles.icon]}/>
          <Spacer sizePx={8} css={FLEX_COLUMN} />
          <Msg str={allSelected ? Str.DownloadAll : Str.DownloadSelected} css={FLEX_COLUMN} />
        </a>
      </LocalDiv>
    </ReportRecipientLayout>
  );
};

export default ReportRecipientDashboard;
